import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Modal, Row, Space, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import CustomButton from "components/Common/buttons/CustomButton";
import { getUsersList, updateMiniDepot } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import NGIFormItem from "components/Common/form/NGIFormItem";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { makeStyles } from "@material-ui/core/styles";
import { getEndOfTheDay } from "components/Common/formatter";
import moment from "moment/moment";
import { USER_ROLES } from "utils/constants";
import NGISelect from "components/Common/inputs/NGISelect";
import { Option } from "antd/lib/mentions";
import NGIDatePicker from "components/Common/inputs/NGIDatePicker";

const useStyles = makeStyles({
  titleWithDescription: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  modalContent: {
    padding: "30px 0 10px 0",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 24,
  },
  successContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    width: "100%",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  row: {
    justifyContent: "space-between",
  },
});

const AssignMDSCModal = ({ mainId, mainDepotName, activeItem, onClose, update }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [isLoading, setLoader] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  useEffect(() => {
    if (activeItem) {
      form.setFieldsValue({
        assignedTo: activeItem?.assignedTo,
        assignedUntil: activeItem?.assignedUntil ? moment(activeItem?.assignedUntil) : null,
      });
      setSelectedEmployee(activeItem?.assignedTo);
    }
    getUsersList({
      depot: mainDepotName,
      role: [USER_ROLES.MDSC].join(),
      pageSize: 3000,
    }).then(({ data }) => setUsers(data?.result?.items));
  }, []);

  const handleSave = useCallback(() => {
    const { assignedTo, assignedUntil } = form.getFieldsValue();
    setLoader(true);
    closeConfirmationModal();
    updateMiniDepot(
      {
        assignedTo: assignedTo || null,
        assignedUntil: assignedUntil ? getEndOfTheDay(assignedUntil) : null,
        status: "Active",
      },
      activeItem?.id
    )
      .then(() => {
        setTimeout(() => {
          setLoader(false);
          successMessage(`Mini Depot was ${activeItem ? "updated" : "created"} successfully`);
          onClose();
          update();
        }, 500);
      })
      .catch(error => {
        setLoader(false);
        defaultNgiErrorMethod(error);
      });
  }, [mainId, activeItem, form]);

  return (
    <Modal
      open={true}
      title={
        <div className={classes.titleWithDescription}>
          Add Daily Mini-Depot Stock Clerk
          <span>Select a person who will be the daily mini depot manager</span>
        </div>
      }
      onCancel={onClose}
      closable={false}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <Form name="assignMDSC" form={form} onFinish={openConfirmationModal} className={classes.formContainer}>
          <div className={classes.modalContent}>
            <Row className={classes.row}>
              <Col span={11}>
                <Title level={5}>Select Employee</Title>
                <NGIFormItem name="assignedTo">
                  <NGISelect
                    allowClear
                    loading={!users?.length}
                    onChange={setSelectedEmployee}
                    placeholder="Please select User"
                    filterOption={(input, option) =>
                      (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {users?.map(item => (
                      <Option key={item.email} value={item.email}>
                        {item.name || item.email}
                      </Option>
                    ))}
                  </NGISelect>
                </NGIFormItem>
              </Col>
              <Col span={11}>
                <Title level={5}>Access end date</Title>
                <NGIFormItem name="assignedUntil">
                  <NGIDatePicker disabledPasDates allowClear />
                </NGIFormItem>
              </Col>
            </Row>
          </div>
          <Space>
            <CustomButton onClick={onClose} type="primary" color="outlined" size="small" text="Cancel" />
            <CustomButton
              htmlType="submit"
              type="primary"
              size="small"
              text={selectedEmployee ? "ADD MDSC" : "Save Changes"}
            />
          </Space>
        </Form>
      </Spin>
      {isConfirmationOpen && (
        <ConfirmationModal
          description={`Are you sure you want to ${selectedEmployee ? "add MDSC" : "save changes"}?`}
          onCancel={closeConfirmationModal}
          onConfirm={handleSave}
        />
      )}
    </Modal>
  );
};

export default AssignMDSCModal;
